// src/finalizeFacilityMembership.js

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "./firebase";
import { hasApprovedWorkerReadiness } from "./core/workforce/workerReadiness";

import {
  PLATFORM_ROLES,
  normalizeId,
  normalizePlatformRole,
  getRolePermissions,
} from "./security/permissions";

/*
 * ShiftBridge Canonical Facility Membership Finalizer
 *
 * PURPOSE
 * -------
 * Establish or repair the operational access record for a user who
 * entered ShiftBridge through an authorized invitation.
 *
 * Canonical model:
 *
 *   Firebase Auth
 *        ↓
 *   users/{uid}
 *        ↓
 *   accessProfiles/{uid}
 *        ↓
 *   Operational Scope
 *
 * facilityProfiles/{facilityId}/planners|workers remains a facility
 * membership/index mirror used for scoped discovery and migration.
 */

/* =========================================================
   NORMALIZATION
   ========================================================= */

function lower(value) {
  return normalizeId(value);
}

function upper(value) {
  return String(value || "")
    .trim()
    .toUpperCase();
}

function uniqueLowerList(values = []) {
  return Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map(lower)
        .filter(Boolean)
        .filter((value) => value !== "all")
    )
  );
}

/*
 * =========================================================
 * TEMP PROVISIONING STAGE DIAGNOSTIC
 * =========================================================
 *
 * Identifies the exact Firestore operation producing
 * permission-denied during facility access repair.
 *
 * REMOVE after Phase 5 provisioning regression.
 */

async function runProvisioningStage(
  stage,
  details,
  operation
) {
  console.log(
    `PROVISIONING STAGE START — ${stage}`,
    details
  );

  try {
    const result =
      await operation();

    console.log(
      `PROVISIONING STAGE SUCCESS — ${stage}`,
      details
    );

    return result;
  } catch (error) {
    console.error(
      `PROVISIONING STAGE ERROR — ${stage}`,
      {
        ...details,
        code:
          error?.code || "",
        message:
          error?.message ||
          String(error || ""),
        error,
      }
    );

    throw error;
  }
}

/*
 * =========================================================
 * TENANT / FACILITY INTEGRITY
 * =========================================================
 *
 * accessProfiles/{uid} must never be created from facility
 * IDs that merely arrived in an invite or legacy user doc.
 *
 * Every facility must exist and its canonical tenant must
 * match the tenant being provisioned.
 */

async function assertFacilitiesBelongToTenant({
  tenantId,
  facilityIds = [],
}) {
  const cleanTenantId =
    lower(tenantId);

  const cleanFacilityIds =
    uniqueLowerList(
      facilityIds
    );

  if (!cleanTenantId) {
    throw new Error(
      "Cannot validate facility scope: missing tenantId."
    );
  }

  if (
    cleanFacilityIds.length === 0
  ) {
    throw new Error(
      "Cannot validate facility scope: no facilities supplied."
    );
  }

  await Promise.all(
    cleanFacilityIds.map(
      async (facilityId) => {
        const facilityRef =
          doc(
            db,
            "facilityProfiles",
            facilityId
          );

        const facilitySnap =
          await getDoc(
            facilityRef
          );

        if (
          !facilitySnap.exists()
        ) {
          throw new Error(
            `Cannot finalize access: facility profile "${facilityId}" does not exist.`
          );
        }

        const facilityData =
          facilitySnap.data() || {};

        const facilityTenantId =
          lower(
            facilityData?.tenantId ||
              facilityData?.tenantKey ||
              ""
          );

        if (!facilityTenantId) {
          throw new Error(
            `Cannot finalize access: facility "${facilityId}" is missing tenantId.`
          );
        }

        if (
          facilityTenantId !==
          cleanTenantId
        ) {
          throw new Error(
            `Cannot finalize access: facility "${facilityId}" belongs to tenant "${facilityTenantId}", not "${cleanTenantId}".`
          );
        }
      }
    )
  );

  return true;
}

/* =========================================================
   CREDENTIAL NORMALIZATION
   ========================================================= */

function normalizeCredentials(credentials = []) {
  if (!Array.isArray(credentials)) {
    return [];
  }

  const byType = new Map();

  for (const credential of credentials) {
    let normalized = null;

    if (typeof credential === "string") {
      const type = upper(credential);

      if (type) {
        normalized = {
          type,
          verified: true,
          expiresAt: null,
        };
      }
    } else if (
      credential &&
      typeof credential === "object"
    ) {
      const type = upper(
        credential.type ||
          credential.credential ||
          ""
      );

      if (type) {
        normalized = {
          type,
          verified:
            credential.verified === true,
          expiresAt:
            credential.expiresAt ?? null,
        };
      }
    }

    if (!normalized) {
      continue;
    }

    const previous =
      byType.get(normalized.type);

    if (!previous) {
      byType.set(
        normalized.type,
        normalized
      );
      continue;
    }

    const previousMs =
      toComparableMillis(
        previous.expiresAt
      );

    const normalizedMs =
      toComparableMillis(
        normalized.expiresAt
      );

    const previousScore =
      (previous.verified ? 1 : 0) *
        1_000_000_000_000 +
      previousMs;

    const normalizedScore =
      (normalized.verified ? 1 : 0) *
        1_000_000_000_000 +
      normalizedMs;

    if (
      normalizedScore >= previousScore
    ) {
      byType.set(
        normalized.type,
        normalized
      );
    }
  }

  return Array.from(
    byType.values()
  ).sort((a, b) =>
    a.type.localeCompare(b.type)
  );
}

function toComparableMillis(value) {
  if (!value) {
    return 0;
  }

  if (
    typeof value?.toMillis === "function"
  ) {
    const milliseconds =
      value.toMillis();

    return Number.isFinite(milliseconds)
      ? milliseconds
      : 0;
  }

  if (
    typeof value?.toDate === "function"
  ) {
    const milliseconds =
      value.toDate().getTime();

    return Number.isFinite(milliseconds)
      ? milliseconds
      : 0;
  }

  const milliseconds =
    new Date(value).getTime();

  return Number.isFinite(milliseconds)
    ? milliseconds
    : 0;
}

/* =========================================================
   ROLE CLASSIFICATION
   ========================================================= */

function isSelfProvisionableRole(role) {
  return [
    PLATFORM_ROLES.FACILITY_PLANNER,
    PLATFORM_ROLES.FACILITY_CLIENT,
    PLATFORM_ROLES.FACILITY_ADMIN_VIEWER,
    PLATFORM_ROLES.WORKER,
  ].includes(role);
}

function getLegacyMembershipCollection(
  role
) {
  if (
    role ===
    PLATFORM_ROLES.FACILITY_PLANNER
  ) {
    return "planners";
  }

  if (
    role === PLATFORM_ROLES.WORKER
  ) {
    return "workers";
  }

  return "";
}

/*
 * =========================================================
 * LEGACY ACCESS PROFILE REPAIR SCOPE
 * =========================================================
 *
 * Legacy repair is intentionally conservative.
 *
 * We may canonicalize:
 *
 * - historical role aliases;
 * - permission maps;
 * - scope/version markers;
 * - equivalent facility field shapes.
 *
 * We must NOT invent a tenant or facility that is missing
 * from an already-existing operational access record.
 */

function getLegacyAccessRepairScope(
  accessProfile = {}
) {
  const facilityIds =
    uniqueLowerList([
      accessProfile?.facilityId,
      accessProfile?.primaryFacilityId,

      ...(Array.isArray(
        accessProfile?.facilityIds
      )
        ? accessProfile.facilityIds
        : []),

      ...(Array.isArray(
        accessProfile?.allowedFacilityIds
      )
        ? accessProfile.allowedFacilityIds
        : []),

      ...(Array.isArray(
        accessProfile?.assignedFacilityIds
      )
        ? accessProfile.assignedFacilityIds
        : []),
    ]);

  const facilityId =
    lower(
      accessProfile?.primaryFacilityId ||
        accessProfile?.facilityId ||
        facilityIds[0] ||
        ""
    );

  const finalFacilityIds =
    uniqueLowerList([
      facilityId,
      ...facilityIds,
    ]);

  return {
    tenantId:
      lower(
        accessProfile?.tenantId ||
          accessProfile?.tenantKey ||
          ""
      ),

    facilityId,

    primaryFacilityId:
      facilityId,

    facilityIds:
      finalFacilityIds,
  };
}

/*
 * =========================================================
 * USER IDENTITY INTEGRITY
 * =========================================================
 *
 * users/{uid} is the canonical profile/lifecycle identity.
 *
 * finalizeFacilityMembership() may create or repair
 * operational authorization, but it must never:
 *
 * - invent an operational account without users/{uid};
 * - change an existing user's canonical role;
 * - move an existing user into another tenant;
 * - attach access to a different email identity.
 *
 * Historical role aliases are normalized before comparison.
 * tenantName is intentionally never used as tenant identity.
 */

async function assertUserIdentityMatchesAccessRequest({
  uid,
  email,
  role,
  tenantId,
}) {
  const userRef =
    doc(
      db,
      "users",
      uid
    );

  const userSnap =
    await getDoc(
      userRef
    );

  if (!userSnap.exists()) {
    throw new Error(
      `Cannot finalize access: users/${uid} does not exist.`
    );
  }

  const userData =
    userSnap.data() || {};


  /* =====================================================
     UID INTEGRITY
     ===================================================== */

  const storedUid =
    String(
      userData?.uid || ""
    ).trim();

  if (
    storedUid &&
    storedUid !== uid
  ) {
    throw new Error(
      `Cannot finalize access: users/${uid} contains a conflicting uid.`
    );
  }


  /* =====================================================
     EMAIL INTEGRITY
     ===================================================== */

  const storedEmail =
    lower(
      userData?.email ||
        userData?.emailLower ||
        ""
    );

  if (
    storedEmail &&
    email &&
    storedEmail !== email
  ) {
    throw new Error(
      `Cannot finalize access: user email "${storedEmail}" does not match "${email}".`
    );
  }


  /* =====================================================
     ROLE INTEGRITY
     ===================================================== */

  const storedRole =
    normalizePlatformRole(
      userData?.platformRole ||
        userData?.role ||
        ""
    );

  if (!storedRole) {
    throw new Error(
      `Cannot finalize access: users/${uid} is missing a canonical role.`
    );
  }

  if (
    storedRole !== role
  ) {
    throw new Error(
      `Cannot finalize access: users/${uid} role "${storedRole}" does not match requested role "${role}".`
    );
  }


  /* =====================================================
     TENANT INTEGRITY
     ===================================================== */

  const storedTenantId =
    lower(
      userData?.tenantId ||
        userData?.tenantKey ||
        ""
    );

  if (!storedTenantId) {
    throw new Error(
      `Cannot finalize access: users/${uid} is missing tenantId.`
    );
  }

  if (
    storedTenantId !==
    tenantId
  ) {
    throw new Error(
      `Cannot finalize access: users/${uid} belongs to tenant "${storedTenantId}", not "${tenantId}".`
    );
  }

  if (role === PLATFORM_ROLES.WORKER && !hasApprovedWorkerReadiness(userData)) {
    throw new Error("Worker onboarding and compliance approval are required before operational access.");
  }
  return userData;
}

/*
 * =========================================================
 * EXISTING ACCESS PROFILE INTEGRITY
 * =========================================================
 *
 * Repairing operational access may update facility scope,
 * status, permissions, or missing fields.
 *
 * It must never silently change an already-established:
 *
 * - uid
 * - email identity
 * - canonical role
 * - tenant
 *
 * Facility assignments are intentionally not compared here.
 * They may legitimately change as long as every new facility
 * is later proven to belong to the same tenant.
 */

async function assertExistingAccessProfileMatchesRequest({
  uid,
  email,
  role,
  tenantId,
}) {
  const accessRef =
    doc(
      db,
      "accessProfiles",
      uid
    );

  const accessSnap =
    await getDoc(
      accessRef
    );

  /*
   * No existing access profile is valid for first-time
   * provisioning. The caller may continue and create it.
   */
  if (!accessSnap.exists()) {
    return null;
  }

  const accessData =
    accessSnap.data() || {};


  /* =====================================================
     UID INTEGRITY
     ===================================================== */

  const storedUid =
    String(
      accessData?.uid || ""
    ).trim();

  if (
    storedUid &&
    storedUid !== uid
  ) {
    throw new Error(
      `Cannot finalize access: accessProfiles/${uid} contains a conflicting uid.`
    );
  }


  /* =====================================================
     EMAIL INTEGRITY
     ===================================================== */

  const storedEmail =
    lower(
      accessData?.email || ""
    );

  if (
    storedEmail &&
    email &&
    storedEmail !== email
  ) {
    throw new Error(
      `Cannot finalize access: existing access-profile email "${storedEmail}" does not match "${email}".`
    );
  }


  /* =====================================================
     ROLE INTEGRITY
     ===================================================== */

  const storedRole =
    normalizePlatformRole(
      accessData?.platformRole ||
        accessData?.role ||
        ""
    );

  /*
   * Missing role may be repaired.
   * A conflicting established role may not.
   */
  if (
    storedRole &&
    storedRole !== role
  ) {
    throw new Error(
      `Cannot finalize access: existing access-profile role "${storedRole}" does not match requested role "${role}".`
    );
  }


  /* =====================================================
     TENANT INTEGRITY
     ===================================================== */

  const storedTenantId =
    lower(
      accessData?.tenantId ||
        accessData?.tenantKey ||
        ""
    );

  /*
   * Missing tenant may be repaired.
   * A conflicting established tenant may not.
   */
  if (
    storedTenantId &&
    storedTenantId !== tenantId
  ) {
    throw new Error(
      `Cannot finalize access: existing access profile belongs to tenant "${storedTenantId}", not "${tenantId}".`
    );
  }

  return accessData;
}

/* =========================================================
   ACCESS PROFILE
   ========================================================= */

async function writeAccessProfile({
  uid,
  email,
  role,
  tenantId,
  facilityId,
  facilityIds,
  status,
  disabled,
  inviteId,
  source,
}) {
  if (role === PLATFORM_ROLES.WORKER) {
    const worker = await getDoc(doc(db, "users", uid));
    if (!worker.exists() || !hasApprovedWorkerReadiness(worker.data())) {
      throw new Error("Worker onboarding and compliance approval are required before operational access.");
    }
  }
  const accessRef = doc(
    db,
    "accessProfiles",
    uid
  );

  const existingSnap =
    await getDoc(accessRef);

  const existing =
    existingSnap.exists()
      ? existingSnap.data() || {}
      : {};

  if (role === PLATFORM_ROLES.WORKER && existingSnap.exists() &&
      (existing.active === false || existing.disabled === true || existing.archived === true ||
        (existing.status && existing.status !== "active"))) {
    throw new Error("Revoked Worker access requires explicit administrative reactivation.");
  }

  const permissions =
    getRolePermissions(role);

  const payload = {
    uid,
    email,

    role,
    platformRole: role,

    tenantId,

    facilityId,
    primaryFacilityId: facilityId,

    facilityIds,
    allowedFacilityIds: facilityIds,

    active: disabled !== true,
    disabled: disabled === true,

    status: String(
      status || "active"
    )
      .trim()
      .toLowerCase(),

    /*
     * IMPORTANT:
     * permissions must replace the complete legacy permissions map.
     * updateDoc({ permissions }) replaces the top-level permissions
     * field instead of recursively preserving obsolete permission keys.
     */
    permissions,

    scopeVersion: 1,
    permissionVersion: 1,

    source:
      String(source || "invite_registration")
        .trim()
        .toLowerCase(),

    updatedAt: serverTimestamp(),
  };

  if (inviteId) {
    payload.inviteId =
      String(inviteId).trim();
  }
  
  if (!existing.createdAt) {
    payload.createdAt =
      serverTimestamp();
  }

  /*
 * TEMP ACCESS PROFILE PAYLOAD AUDIT.
 * REMOVE after Phase 5 regression.
 */
console.log(
  "ACCESS PROFILE WRITE PAYLOAD AUDIT",
  {
    path:
      `accessProfiles/${uid}`,

    existsBeforeWrite:
      existingSnap.exists(),

    uid:
      payload.uid,

    email:
      payload.email,

    role:
      payload.role,

    platformRole:
      payload.platformRole,

    tenantId:
      payload.tenantId,

    facilityId:
      payload.facilityId,

    primaryFacilityId:
      payload.primaryFacilityId,

    facilityIds:
      payload.facilityIds,

    allowedFacilityIds:
      payload.allowedFacilityIds,

    active:
      payload.active,

    disabled:
      payload.disabled,

    status:
      payload.status,

    scopeVersion:
      payload.scopeVersion,

    permissionVersion:
      payload.permissionVersion,

    source:
      payload.source,

    permissionKeys:
      Object.keys(
        payload.permissions || {}
      ).sort(),

  permissions:
  payload.permissions,

permissionsJson:
  JSON.stringify(
    payload.permissions || {},
    null,
    2
  ),
  }
);

  if (existingSnap.exists()) {
    await updateDoc(
      accessRef,
      payload
    );
  } else {
    await setDoc(
      accessRef,
      payload
    );
  }

  return {
    ...existing,
    ...payload,
  };
}

/* =========================================================
   FACILITY MEMBERSHIP / ROSTER MIRROR
   ========================================================= */

async function mirrorLegacyMembership({
  uid,
  email,
  role,
  tenantId,
  facilityIds,
  status,
  credentials,
  disabled,
}) {
  const collectionName =
    getLegacyMembershipCollection(role);

  if (!collectionName) {
    return {
      attempted: false,
      collectionName: "",
      failures: [],
    };
  }

  const normalizedCredentials =
    normalizeCredentials(credentials);

  const results =
    await Promise.allSettled(
      facilityIds.map(async (fid) => {
        const ref = doc(
          db,
          "facilityProfiles",
          fid,
          collectionName,
          uid
        );

        let existing = {};

        try {
          const snap =
            await getDoc(ref);

          if (snap.exists()) {
            existing =
              snap.data() || {};
          }
        } catch (error) {
          /*
           * The mirror is secondary to the canonical access profile.
           * A read can be denied by an older deployed ruleset even when
           * the subsequent canonicalized write is permitted.
           */
        }

        const payload = {
          uid,
          email,

          role,
          platformRole: role,

          tenantId,

          status: String(
            status || "active"
          )
            .trim()
            .toLowerCase(),

          disabled:
            disabled === true,

          facilityId: fid,
          primaryFacilityId:
            facilityIds[0] || fid,

          facilityIds,
          allowedFacilityIds:
            facilityIds,

          updatedAt:
            serverTimestamp(),

          mirroredAt:
            serverTimestamp(),
        };

        if (
          collectionName === "workers"
        ) {
          payload.credentials =
            normalizedCredentials;
        }

        if (!existing.createdAt) {
          payload.createdAt =
            serverTimestamp();
        }

        await setDoc(
          ref,
          payload,
          { merge: true }
        );

        return fid;
      })
    );

  const failures = results
    .map((result, index) => ({
      result,
      facilityId:
        facilityIds[index],
    }))
    .filter(
      ({ result }) =>
        result.status === "rejected"
    );

  if (failures.length > 0) {
    console.warn(
      "Facility membership mirror incomplete.",
      failures.map(
        ({ facilityId, result }) => ({
          facilityId,
          error:
            result.reason?.message ||
            String(result.reason || ""),
        })
      )
    );
  }

  return {
    attempted: true,
    collectionName,
    failures,
  };
}

/* =========================================================
   PUBLIC FINALIZER
   ========================================================= */

export async function finalizeFacilityMembership({
  uid,
  email,

  role,
  platformRole,

  tenantId,

  status = "active",

  facilityIds = [],
  allowedFacilityIds = [],
  facilityId = null,
  primaryFacilityId = null,

  credentials = [],

  disabled = false,

  inviteId = "",

  /*
   * Canonical provenance marker.
   *
   * Normal invite completion uses "invite_registration".
   * MainApp legacy repair uses "legacy_access_repair".
   */
  source = "invite_registration",
}) {
  const cleanUid =
    String(uid || "").trim();

  const cleanEmail =
    lower(email);

  const normalizedRole =
    normalizePlatformRole(
      platformRole ||
        role ||
        ""
    );

  const cleanTenantId =
    lower(tenantId);

  const normalizedFacilityIds =
    uniqueLowerList([
      ...(Array.isArray(facilityIds)
        ? facilityIds
        : []),

      ...(Array.isArray(
        allowedFacilityIds
      )
        ? allowedFacilityIds
        : []),

      facilityId,
      primaryFacilityId,
    ]);

  const resolvedPrimaryFacilityId =
    lower(
      primaryFacilityId ||
        facilityId
    ) ||
    normalizedFacilityIds[0] ||
    "";

  /* =====================================================
     VALIDATION
     ===================================================== */

  if (!cleanUid) {
    throw new Error(
      "Cannot finalize access: missing uid."
    );
  }

  if (!cleanEmail) {
    throw new Error(
      "Cannot finalize access: missing email."
    );
  }

  if (!normalizedRole) {
    throw new Error(
      "Cannot finalize access: missing role."
    );
  }

  if (
    !isSelfProvisionableRole(
      normalizedRole
    )
  ) {
    throw new Error(
      `Cannot self-provision operational access for role "${normalizedRole}".`
    );
  }

  if (!cleanTenantId) {
    throw new Error(
      "Cannot finalize access: missing tenantId."
    );
  }

  if (
    !resolvedPrimaryFacilityId ||
    normalizedFacilityIds.length === 0
  ) {
    throw new Error(
      "Cannot finalize access: missing facility scope."
    );
  }

const finalFacilityIds =
  uniqueLowerList([
    resolvedPrimaryFacilityId,
    ...normalizedFacilityIds,
  ]);


/* =====================================================
   USER IDENTITY INTEGRITY
   ===================================================== */

/*
 * Operational authorization must agree with the
 * existing canonical users/{uid} identity before
 * accessProfiles/{uid} is created or repaired.
 */
await runProvisioningStage(
  "READ / VALIDATE users/{uid}",
  {
    uid:
      cleanUid,

    role:
      normalizedRole,

    tenantId:
      cleanTenantId,
  },
  () =>
    assertUserIdentityMatchesAccessRequest({
      uid:
        cleanUid,

      email:
        cleanEmail,

      role:
        normalizedRole,

      tenantId:
        cleanTenantId,
    })
);


/* =====================================================
   EXISTING ACCESS PROFILE INTEGRITY
   ===================================================== */

/*
 * If operational access already exists, this may repair
 * incomplete fields or update facility membership, but it
 * may not change the account's established role or tenant.
 */
await runProvisioningStage(
  "READ accessProfiles/{uid}",
  {
    uid:
      cleanUid,

    role:
      normalizedRole,

    tenantId:
      cleanTenantId,
  },
  () =>
    assertExistingAccessProfileMatchesRequest({
      uid:
        cleanUid,

      email:
        cleanEmail,

      role:
        normalizedRole,

      tenantId:
        cleanTenantId,
    })
);


/* =====================================================
   TENANT / FACILITY INTEGRITY
   ===================================================== */

/*
 * After proving the user belongs to the requested
 * tenant, prove that every requested facility also
 * belongs to that same tenant.
 */
await runProvisioningStage(
  "READ / VALIDATE facilityProfiles",
  {
    uid:
      cleanUid,

    tenantId:
      cleanTenantId,

    facilityIds:
      finalFacilityIds,
  },
  () =>
    assertFacilitiesBelongToTenant({
      tenantId:
        cleanTenantId,

      facilityIds:
        finalFacilityIds,
    })
);


/* =====================================================
   1. REQUIRED CANONICAL ACCESS PROFILE
   ===================================================== */

  const accessProfile =
  await runProvisioningStage(
    "CREATE / UPDATE accessProfiles/{uid}",
    {
      uid:
        cleanUid,

      role:
        normalizedRole,

      tenantId:
        cleanTenantId,

      facilityId:
        resolvedPrimaryFacilityId,

      facilityIds:
        finalFacilityIds,

      source,
    },
    () =>
      writeAccessProfile({
        uid:
          cleanUid,

        email:
          cleanEmail,

        role:
          normalizedRole,

        tenantId:
          cleanTenantId,

        facilityId:
          resolvedPrimaryFacilityId,

        facilityIds:
          finalFacilityIds,

        status,
        disabled,
        inviteId,
        source,
      })
  );

  /* =====================================================
     2. FACILITY MEMBERSHIP / ROSTER MIRROR
     ===================================================== */

  const legacyMirror =
    await mirrorLegacyMembership({
      uid: cleanUid,
      email: cleanEmail,

      role: normalizedRole,

      tenantId: cleanTenantId,

      facilityIds:
        finalFacilityIds,

      status,
      credentials,
      disabled,
    });

  return {
    ok: true,

    uid: cleanUid,
    email: cleanEmail,

    role: normalizedRole,

    tenantId: cleanTenantId,

    facilityId:
      resolvedPrimaryFacilityId,

    facilityIds:
      finalFacilityIds,

    allowedFacilityIds:
      finalFacilityIds,

    accessProfile,

    legacyMirror,
  };
}

/*
 * =========================================================
 * PUBLIC LEGACY FACILITY ACCESS REPAIR
 * =========================================================
 *
 * MainApp may detect that a facility account has an older
 * accessProfiles/{uid} record.
 *
 * MainApp should NOT decide how that record is repaired.
 *
 * This provisioning module owns that responsibility.
 */

export async function repairLegacyFacilityAccessProfile({
  uid,
  email,
  userData = {},
  accessProfile = null,
} = {}) {
  const cleanUid =
    String(uid || "").trim();

  const cleanEmail =
    lower(
      email ||
        userData?.email ||
        userData?.emailLower ||
        ""
    );

  if (!cleanUid) {
    throw new Error(
      "Cannot repair legacy facility access: missing uid."
    );
  }

  if (
    !accessProfile ||
    typeof accessProfile !== "object"
  ) {
    throw new Error(
      "Cannot repair legacy facility access: access profile is missing."
    );
  }


  /* =====================================================
     ROLE INTEGRITY
     ===================================================== */

  const userRole =
    normalizePlatformRole(
      userData?.platformRole ||
        userData?.role ||
        ""
    );

  const accessRole =
    normalizePlatformRole(
      accessProfile?.platformRole ||
        accessProfile?.role ||
        ""
    );

  if (!userRole) {
    throw new Error(
      "Cannot repair legacy facility access: user profile is missing a canonical role."
    );
  }

  if (!accessRole) {
    throw new Error(
      "Cannot repair legacy facility access: access profile is missing a recognizable role."
    );
  }

  if (
    userRole !==
    accessRole
  ) {
    throw new Error(
      `Cannot repair legacy facility access: user role "${userRole}" conflicts with access role "${accessRole}".`
    );
  }

  if (
    ![
      PLATFORM_ROLES.FACILITY_PLANNER,
      PLATFORM_ROLES.FACILITY_CLIENT,
      PLATFORM_ROLES.FACILITY_ADMIN_VIEWER,
    ].includes(accessRole)
  ) {
    throw new Error(
      `Cannot repair legacy facility access for role "${accessRole}".`
    );
  }


  /* =====================================================
     EXISTING OPERATIONAL SCOPE
     ===================================================== */

  const repairScope =
    getLegacyAccessRepairScope(
      accessProfile
    );

  if (!repairScope.tenantId) {
    throw new Error(
      "Cannot repair legacy facility access: existing access profile is missing tenantId."
    );
  }

  if (
    !repairScope.facilityId ||
    repairScope.facilityIds.length === 0
  ) {
    throw new Error(
      "Cannot repair legacy facility access: existing access profile is missing facility scope."
    );
  }


  /* =====================================================
     CANONICAL FINALIZATION
     ===================================================== */

  const result =
    await finalizeFacilityMembership({
      uid:
        cleanUid,

      email:
        cleanEmail,

      role:
        accessRole,

      platformRole:
        accessRole,

      tenantId:
        repairScope.tenantId,

      status:
        String(
          accessProfile?.status ||
            userData?.status ||
            "active"
        )
          .trim()
          .toLowerCase(),

      facilityId:
        repairScope.facilityId,

      primaryFacilityId:
        repairScope.primaryFacilityId,

      facilityIds:
        repairScope.facilityIds,

      allowedFacilityIds:
        repairScope.facilityIds,

      credentials:
        Array.isArray(
          userData?.credentials
        )
          ? userData.credentials
          : [],

      disabled:
        accessProfile?.disabled ===
          true ||
        userData?.disabled ===
          true,

      inviteId:
        accessProfile?.inviteId ||
        "",

      source:
        "legacy_access_repair",
    });

  return {
    repaired: true,

    role:
      accessRole,

    tenantId:
      repairScope.tenantId,

    facilityId:
      repairScope.facilityId,

    facilityIds:
      repairScope.facilityIds,

    result,
  };
}

/*
 * =========================================================
 * MISSING FACILITY ACCESS PROFILE REPAIR
 * =========================================================
 *
 * Legacy ShiftBridge facility accounts may have a complete
 * canonical users/{uid} record but no accessProfiles/{uid}.
 *
 * This repair does NOT invent identity from tenantName or
 * from an incomplete legacy invitation.
 *
 * Authority is reconstructed only when:
 *
 * - users/{uid} already owns a supported facility role;
 * - users/{uid} has canonical tenantId;
 * - users/{uid} already has concrete facility membership;
 * - finalizeFacilityMembership() independently proves that
 *   every facility belongs to that tenant.
 */

export async function repairMissingFacilityAccessProfile({
  uid,
  email,
  userData = {},
} = {}) {
  const cleanUid =
    String(uid || "").trim();

  const cleanEmail =
    lower(
      email ||
        userData?.email ||
        userData?.emailLower ||
        ""
    );

  if (!cleanUid) {
    throw new Error(
      "Cannot repair missing facility access: missing uid."
    );
  }


  /* =====================================================
     ROLE
     ===================================================== */

  const role =
    normalizePlatformRole(
      userData?.platformRole ||
        userData?.role ||
        ""
    );

  if (
    ![
      PLATFORM_ROLES.FACILITY_PLANNER,
      PLATFORM_ROLES.FACILITY_CLIENT,
      PLATFORM_ROLES.FACILITY_ADMIN_VIEWER,
    ].includes(role)
  ) {
    throw new Error(
      `Cannot repair missing facility access for role "${role || "unknown"}".`
    );
  }


  /* =====================================================
     TENANT
     ===================================================== */

  const tenantId =
    lower(
      userData?.tenantId ||
        userData?.tenantKey ||
        ""
    );

  if (!tenantId) {
    throw new Error(
      "Cannot repair missing facility access: users/{uid} is missing tenantId."
    );
  }


  /* =====================================================
     FACILITY SCOPE
     ===================================================== */

  const facilityIds =
    uniqueLowerList([
      userData?.facilityId,
      userData?.primaryFacilityId,

      ...(Array.isArray(
        userData?.facilityIds
      )
        ? userData.facilityIds
        : []),

      ...(Array.isArray(
        userData?.allowedFacilityIds
      )
        ? userData.allowedFacilityIds
        : []),

      ...(Array.isArray(
        userData?.assignedFacilityIds
      )
        ? userData.assignedFacilityIds
        : []),
    ]);

  const facilityId =
    lower(
      userData?.primaryFacilityId ||
        userData?.facilityId ||
        facilityIds[0] ||
        ""
    );

  const finalFacilityIds =
    uniqueLowerList([
      facilityId,
      ...facilityIds,
    ]);

  if (
    !facilityId ||
    finalFacilityIds.length === 0
  ) {
    throw new Error(
      "Cannot repair missing facility access: users/{uid} is missing facility scope."
    );
  }


  /* =====================================================
     CANONICAL FINALIZATION
     ===================================================== */

  /*
   * finalizeFacilityMembership() performs the stronger
   * checks built earlier:
   *
   * users/{uid} role / tenant integrity
   *         ↓
   * existing access-profile integrity
   *         ↓
   * every facilityProfiles/{id}.tenantId must match
   *         ↓
   * canonical access profile write
   */

  const result =
    await finalizeFacilityMembership({
      uid:
        cleanUid,

      email:
        cleanEmail,

      role,

      platformRole:
        role,

      tenantId,

      status:
        String(
          userData?.status ||
            "active"
        )
          .trim()
          .toLowerCase(),

      facilityId,

      primaryFacilityId:
        facilityId,

      facilityIds:
        finalFacilityIds,

      allowedFacilityIds:
        finalFacilityIds,

      credentials:
        Array.isArray(
          userData?.credentials
        )
          ? userData.credentials
          : [],

      disabled:
        userData?.disabled === true,

      inviteId:
        userData?.inviteId ||
        "",

      source:
        "missing_access_profile_repair",
    });

  return {
    repaired: true,

    repairType:
      "missing_access_profile",

    role,

    tenantId,

    facilityId,

    facilityIds:
      finalFacilityIds,

    result,
  };
}
